import { useState, useEffect } from 'react';
import { api } from '../api';
import {
  PageHeader, Card, Stat, Button, Field, Input, Table, THead, TH, TR, TD, Empty, SourceNote,
  fmtR, fmtDate, thisMonthISO,
} from '../components/ui';

const CAC_SOURCES = [
  { source: 'Magnetic', label: 'New clients', detail: 'first-time clients with a project starting in the period' },
  { source: 'App', label: 'Marketing & other spend', detail: 'entered monthly below (no marketing-platform connector yet)' },
  { source: 'Derived', label: 'CAC', detail: 'total acquisition spend ÷ new clients won' },
];

export default function CAC() {
  const [period, setPeriod] = useState('year');
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [form, setForm] = useState({ period_month: thisMonthISO(), marketing_spend: '', other_acq_cost: '', notes: '' });
  const [msg, setMsg] = useState(null);

  const load = () => {
    api.cac.summary(period).then(setSummary).catch(() => {});
    api.cac.monthly().then(setMonthly).catch(() => {});
  };
  useEffect(load, [period]);

  const save = async () => {
    setMsg(null);
    try {
      await api.cac.saveMonthly({
        period_month: form.period_month,
        marketing_spend: parseFloat(form.marketing_spend) || 0,
        other_acq_cost: parseFloat(form.other_acq_cost) || 0,
        sales_time_pct: 100,
        notes: form.notes || null,
      });
      setForm({ ...form, marketing_spend: '', other_acq_cost: '', notes: '' });
      load();
      setMsg({ kind: 'ok', text: `Saved ${form.period_month}` });
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
  };

  const PERIODS = [['year', 'Year'], ['ytd', 'YTD'], ['all', 'All']];

  return (
    <div>
      <PageHeader
        eyebrow="Acquisition"
        title="Cost of acquisition"
        subtitle="New clients won vs the spend to win them. Sales/BD salary time isn't auto-attributed yet — record it under other acquisition cost."
        actions={
          <div className="flex bg-slate-800 border border-slate-700 rounded-md p-0.5 gap-0.5">
            {PERIODS.map(([v, l]) => (
              <button key={v} onClick={() => setPeriod(v)}
                className={`px-3 py-1.5 text-sm font-medium rounded ${period === v ? 'bg-brand-500 text-white' : 'text-slate-500 hover:text-slate-100'}`}>
                {l}
              </button>
            ))}
          </div>
        }
      />

      <SourceNote items={CAC_SOURCES} />

      {summary && (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
            <Stat label="New clients" value={summary.new_clients} sub={summary.label}
              info="First-time clients with a project starting in the period (Magnetic)." />
            <Stat label="Acquisition spend" value={fmtR(summary.total_acq_cost)} sub="marketing + other"
              info="Marketing + other acquisition cost you entered for the period." />
            <Stat label="CAC" value={summary.cac != null ? fmtR(summary.cac) : '—'} sub="per new client"
              info="Acquisition spend ÷ new clients. '—' when no new clients that period." />
            <Stat label="Marketing" value={fmtR(summary.marketing_spend)} sub={`other ${fmtR(summary.other_acq_cost)}`} />
          </div>
          {summary.partial_inputs && (
            <p className="text-xs text-amber-600 -mt-4 mb-6">{summary.note}</p>
          )}
        </>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card title="Record monthly acquisition spend">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Month"><Input type="month" value={form.period_month} onChange={(e) => setForm({ ...form, period_month: e.target.value })} /></Field>
            <Field label="Marketing (R)"><Input type="number" value={form.marketing_spend} onChange={(e) => setForm({ ...form, marketing_spend: e.target.value })} /></Field>
            <Field label="Other acq cost (R)"><Input type="number" value={form.other_acq_cost} onChange={(e) => setForm({ ...form, other_acq_cost: e.target.value })} /></Field>
            <div className="flex items-end"><Button onClick={save}>Save month</Button></div>
          </div>
          <Field label="Notes" className="mt-3"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Q1 LinkedIn campaign + BD salaries" /></Field>
          {msg && <p className={`text-xs mt-3 ${msg.kind === 'ok' ? 'text-emerald-600' : 'text-brand-500'}`}>{msg.text}</p>}
          <div className="mt-4">
            {monthly.length === 0 ? <Empty>No acquisition spend recorded.</Empty> : (
              <Table>
                <THead><TH>Month</TH><TH align="right">Marketing</TH><TH align="right">Other</TH><TH>By</TH></THead>
                <tbody>
                  {monthly.map((r) => (
                    <TR key={r.period_month}>
                      <TD mono>{r.period_month}</TD>
                      <TD align="right" mono>{fmtR(r.marketing_spend)}</TD>
                      <TD align="right" mono>{fmtR(r.other_acq_cost)}</TD>
                      <TD muted>{r.entered_by || '—'}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </Card>

        <Card title={`New clients · ${summary?.new_clients || 0}`} subtitle={summary?.label}>
          {summary?.new_client_list?.length ? (
            <Table>
              <THead><TH>Client</TH><TH>First engaged</TH></THead>
              <tbody>
                {summary.new_client_list.map((c) => (
                  <TR key={c.client_name}><TD>{c.client_name}</TD><TD muted mono>{fmtDate(c.first_start)}</TD></TR>
                ))}
              </tbody>
            </Table>
          ) : <Empty>No new clients in this period.</Empty>}
        </Card>
      </div>
    </div>
  );
}
